import { ApolloLink } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';

// The rule for every page (owner decision 31/10/2026): the first time shows its skeleton; every time after, the last copy
// shows at once, a read always runs behind it, and the new data replaces it when it lands. Apollo already keeps every read in
// memory and draws it at once, but cache-first never reads again: what Alexa logged, or what you changed on another page,
// stayed out until a reload. So opening a page, or the app coming back to the screen on it, reads again what it shows.
// refetch() leaves `loading` alone (no notifyOnNetworkStatusChange), so no skeleton comes back and no page has to know.

// These lists read first when a write changed them: they never come back without what you just did
const LIST_WRITES = { WorkoutLogs: ['CreateWorkoutLog', 'DeleteWorkoutLog'] };

const readAt = new Map();
const changedAt = new Map();

const keyOf = (name, variables) => `${name}|${JSON.stringify(variables ?? {})}`;

const isMutation = (query) => {
  const main = getMainDefinition(query);
  return main.kind === 'OperationDefinition' && main.operation === 'mutation';
};

/** Notes when every read landed and when a write changed a list. */
export const freshnessLink = new ApolloLink((operation, forward) =>
  forward(operation).map((result) => {
    const now = Date.now();
    if (!isMutation(operation.query)) {
      readAt.set(keyOf(operation.operationName, operation.variables), now);
    } else if (!result.errors?.length) {
      Object.entries(LIST_WRITES).forEach(([list, writes]) => {
        if (writes.includes(operation.operationName)) changedAt.set(list, now);
      });
    }
    return result;
  }),
);

/** Reads again what's on the page, unless it's still on its way. A failed read keeps what's there. */
export const refreshReads = (client) =>
  client
    .refetchQueries({ include: 'active', onQueryUpdated: (query) => !query.getCurrentResult(false).loading })
    .catch(() => undefined);

// A phone keeps the installed app suspended and brings it back on the page it was on, so no navigation reads it again.
// Only the pages that just show what they read: a live workout, the workout editor and the forms never change under the
// person, and neither does a page with a window open.
const RETURN_PAGES = ['/dashboard', '/workouts', '/progress', '/exercises', '/diet', '/calorie-tracker', '/academy'];

const windowOpen = () => document.querySelector('[role="dialog"]') !== null;

/** The app coming back to the screen reads again what the page shows. */
export const installReturnRefresh = (client) => {
  const refresh = () => {
    if (RETURN_PAGES.includes(window.location.pathname) && !windowOpen()) refreshReads(client);
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') refresh();
  };
  const onShow = (event) => {
    if (event.persisted) refresh();
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pageshow', onShow);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pageshow', onShow);
  };
};

/** A write changed this list since it was last read. */
export const listChanged = (name, variables) =>
  changedAt.has(name) && changedAt.get(name) >= (readAt.get(keyOf(name, variables)) ?? 0);

/** The session ended: nothing about it stays. */
export const forgetReads = () => {
  readAt.clear();
  changedAt.clear();
};
